import React from 'react';

import { Modal } from '@/components/Modal';
import { AddSeedByTypeModal } from '@/sections/seeds/components/modals/AddSeedByTypeModal';
import { SeedModalState } from '@/sections/seeds/hooks/useSeedModal';
import { SeedType } from '@/sections/seeds/types';
import { Seed, SeedStatus, SeedStatusLabel } from '@/types';

/**
 * Seeds modals component props
 *
 * Migration Note: Comment modal migrated to shared NoteModal (2025-10-20)
 * - Removed CommentModal import and usage
 * - NoteModal now rendered in index.tsx following Assets pattern
 * - Props cleaned up (removed onClearSeedForComment, onCommentSubmit)
 *
 * File Import Migration Note: File upload removed from AddSeedByTypeModal
 * - Added onOpenImport prop to coordinate with ImportSeedsModal
 * - File imports now handled via ImportSeedsModal flow
 */
export interface SeedsModalsProps {
  /** Modal state from useSeedModal hook */
  modalState: SeedModalState;
  /** Current seed type filter */
  seedType: SeedType;
  /** Seed selected for single comment */
  seedForComment: Seed | null;
  /** Confirmation action handler */
  onConfirmStatusChange: () => Promise<void>;
  /** Refetch seeds callback (for add seed success) */
  onRefetchSeeds: () => void;
}

/**
 * Seeds modals orchestration component
 *
 * Manages modal states and rendering for the Seeds section:
 * - Add Seed modal (with multi-step type selection)
 * - Confirmation modal (for status change confirmations)
 *
 * Note: Comment modal migrated to shared NoteModal component rendered in index.tsx
 *
 * This component centralizes modal rendering logic that was previously
 * scattered in index.tsx, making the main component cleaner and more maintainable.
 *
 * @example
 * ```tsx
 * <SeedsModals
 *   modalState={modalState}
 *   seedType={type}
 *   seedForComment={seedForComment}
 *   onConfirmStatusChange={handleConfirmStatusChange}
 *   onRefetchSeeds={refetchSeeds}
 * />
 * ```
 */
export const SeedsModals: React.FC<SeedsModalsProps> = ({
  modalState,
  seedType: _seedType,
  seedForComment: _seedForComment,
  onConfirmStatusChange,
  onRefetchSeeds,
}) => {
  return (
    <>
      {/* Add Seed Modal - Multi-step modal for creating new seeds */}
      <AddSeedByTypeModal
        open={modalState.addSeed.isOpen}
        onClose={modalState.addSeed.close}
        onSuccess={onRefetchSeeds}
        onOpenImport={modalState.import.open}
      />

      {/* Confirmation Modal - Confirm bulk status changes */}
      <Modal
        title="Confirm Change Status"
        open={!!modalState.confirmation.data}
        onClose={modalState.confirmation.close}
        footer={{
          text: 'Confirm',
          onClick: onConfirmStatusChange,
        }}
      >
        <div className="pb-4">
          {`Are you sure you want to update the status of following seeds to "${
            SeedStatusLabel[modalState.confirmation.data?.status as SeedStatus]
          }"?`}
        </div>
        <div className="scrollbar-dark max-h-[300px] overflow-auto">
          {modalState.confirmation.data?.data.map((seed: Seed) => (
            <div key={seed?.identifier}>{seed?.identifier}</div>
          ))}
        </div>
      </Modal>
    </>
  );
};
